/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or
 * distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly
 * prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this
 * content, please contact the author for proper authorization and agreement.
 */
import {useCallback, useEffect, useRef, useState} from "react";

// A lightweight replacement for `use-debounce`'s useDebouncedCallback used in this project.
// It returns a stable debounced function that delays invoking `callback` until after `delay`
// milliseconds have elapsed since the last time the debounced function was called.
export const MULTISELECT_DEBOUNCE_MS = 300

export type DebouncedCallback<T extends (...args: any[]) => any> = ((...args: Parameters<T>) => void) & {
    cancel: () => void
}

export function useDebouncedCallback<T extends (...args: any[]) => any>(
    callback: T,
    delay: number
): DebouncedCallback<T> {
  const callbackRef = useRef<T>(callback);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep latest callback
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current as unknown as number);
      timerRef.current = null;
    }
  }, []);

  // Clear timer on unmount
  useEffect(() => {
    return () => {
      cancel();
    };
  }, [cancel]);

  const debounced = useCallback((...args: Parameters<T>) => {
    cancel();
    timerRef.current = setTimeout(() => {
      callbackRef.current(...args);
    }, delay);
  }, [cancel, delay]) as DebouncedCallback<T>;

  debounced.cancel = cancel;

  return debounced;
}

export function useDebounce<T>(value: T, delay: number): T {
    const [debouncedValue, setDebouncedValue] = useState<T>(value)

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value)
        }, delay)

        return () => {
            clearTimeout(handler)
        }
    }, [value, delay])

    return debouncedValue
}
